import React, { useState, useEffect } from 'react';
import {
  Dialog, Pane, TextInputField, Textarea, Select, Label, Text
} from 'evergreen-ui';

const STATUS_OPTIONS = [
  { value: 'not_tested', label: 'Not Tested', color: '#8F95B2' },
  { value: 'in_progress', label: 'In Progress', color: '#3366FF' },
  { value: 'tested', label: 'Tested', color: '#52BD94' },
  { value: 'vulnerable', label: 'Vulnerable', color: '#D14343' },
];

const HTTP_METHODS = [
  { value: '', label: 'None', color: '#8F95B2' },
  { value: 'GET', label: 'GET', color: '#61affe' },
  { value: 'POST', label: 'POST', color: '#49cc90' },
  { value: 'PUT', label: 'PUT', color: '#fca130' },
  { value: 'DELETE', label: 'DELETE', color: '#f93e3e' },
  { value: 'PATCH', label: 'PATCH', color: '#50e3c2' },
  { value: 'HEAD', label: 'HEAD', color: '#9012fe' },
  { value: 'OPTIONS', label: 'OPTIONS', color: '#0d5aa7' },
];

const CoverageNodeDialog = ({ isShown, onClose, onConfirm, node, members }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [httpMethod, setHttpMethod] = useState('');
  const [status, setStatus] = useState('not_tested');
  const [assignedUserId, setAssignedUserId] = useState('');

  useEffect(() => {
    if (node) {
      setName(node.name || '');
      setDescription(node.description || '');
      setUrl(node.url || '');
      setHttpMethod(node.http_method || '');
      setStatus(node.status || 'not_tested');
      setAssignedUserId(node.assigned_user_id || '');
    } else {
      setName('');
      setDescription('');
      setUrl('');
      setHttpMethod('');
      setStatus('not_tested');
      setAssignedUserId('');
    }
  }, [node, isShown]);

  const handleConfirm = () => {
    if (!name.trim()) return;
    onConfirm({
      name: name.trim(),
      description: description.trim() || null,
      url: url.trim() || null,
      http_method: httpMethod || null,
      status,
      assigned_user_id: assignedUserId || null,
    });
  };

  const selectedStatus = STATUS_OPTIONS.find(s => s.value === status);

  return (
    <Dialog
      isShown={isShown}
      title={node ? 'Edit Node' : 'Add Node'}
      onCloseComplete={onClose}
      confirmLabel={node ? 'Save Changes' : 'Create Node'}
      onConfirm={handleConfirm}
      isConfirmDisabled={!name.trim()}
      width={520}
    >
      <Pane>
        {/* Name field */}
        <TextInputField
          label="Name"
          placeholder="e.g., Login Page, User Registration, /api/users"
          value={name}
          onChange={e => setName(e.target.value)}
          required
          inputHeight={36}
        />

        {/* Endpoint section */}
        <Pane
          marginBottom={16}
          padding={12}
          background="#F9FAFC"
          borderRadius={6}
          border="1px solid #E6E8F0"
        >
          <Text size={300} fontWeight={600} color="#474D66" display="block" marginBottom={8}>
            Endpoint Details
          </Text>
          <Pane display="flex" gap={8} alignItems="flex-end">
            <Pane flex={0} flexBasis={110}>
              <Label htmlFor="node-method" display="block" marginBottom={4} size={300}>
                Method
              </Label>
              <Select
                id="node-method"
                value={httpMethod}
                onChange={e => setHttpMethod(e.target.value)}
                width="100%"
                height={36}
              >
                {HTTP_METHODS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </Select>
            </Pane>
            <Pane flex={1}>
              <Label htmlFor="node-url" display="block" marginBottom={4} size={300}>
                URL / Path
              </Label>
              <Pane display="flex" alignItems="center">
                {httpMethod && (
                  <Pane
                    paddingX={8}
                    paddingY={6}
                    borderTopLeftRadius={4}
                    borderBottomLeftRadius={4}
                    background={HTTP_METHODS.find(m => m.value === httpMethod)?.color || '#888'}
                    height={36}
                    display="flex"
                    alignItems="center"
                  >
                    <Text
                      size={200}
                      fontWeight={700}
                      fontFamily="mono"
                      color="#fff"
                      style={{ letterSpacing: '0.5px' }}
                    >
                      {httpMethod}
                    </Text>
                  </Pane>
                )}
                <input
                  id="node-url"
                  type="text"
                  placeholder="/api/v1/users or https://example.com/login"
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  style={{
                    flex: 1,
                    height: 36,
                    padding: '0 10px',
                    fontFamily: 'monospace',
                    fontSize: 13,
                    border: '1px solid #D8DAE5',
                    borderRadius: httpMethod ? '0 4px 4px 0' : 4,
                    borderLeft: httpMethod ? 'none' : undefined,
                    outline: 'none',
                  }}
                />
              </Pane>
            </Pane>
          </Pane>
        </Pane>

        {/* Description */}
        <Label htmlFor="node-desc" display="block" marginBottom={4}>Description</Label>
        <Textarea
          id="node-desc"
          placeholder="Optional description of this functionality, component, or endpoint"
          value={description}
          onChange={e => setDescription(e.target.value)}
          marginBottom={16}
          rows={3}
        />

        {/* Status selector */}
        <Pane marginBottom={16}>
          <Label display="block" marginBottom={8}>Status</Label>
          <Pane display="flex" gap={6}>
            {STATUS_OPTIONS.map(opt => {
              const isActive = status === opt.value;
              return (
                <Pane
                  key={opt.value}
                  flex={1}
                  paddingY={8}
                  paddingX={4}
                  borderRadius={6}
                  border={isActive ? `2px solid ${opt.color}` : '2px solid #E6E8F0'}
                  background={isActive ? `${opt.color}10` : 'white'}
                  cursor="pointer"
                  textAlign="center"
                  onClick={() => setStatus(opt.value)}
                  style={{ transition: 'all 0.15s ease' }}
                >
                  <Pane
                    width={8}
                    height={8}
                    borderRadius="50%"
                    backgroundColor={opt.color}
                    marginX="auto"
                    marginBottom={4}
                    style={{
                      boxShadow: isActive ? `0 0 6px ${opt.color}60` : 'none',
                    }}
                  />
                  <Text
                    size={200}
                    fontWeight={isActive ? 600 : 400}
                    color={isActive ? opt.color : '#696F8C'}
                    display="block"
                  >
                    {opt.label}
                  </Text>
                </Pane>
              );
            })}
          </Pane>
        </Pane>

        {/* Assignee */}
        {members && members.length > 0 && (
          <Pane>
            <Label htmlFor="node-assignee" display="block" marginBottom={4}>Assigned Tester</Label>
            <Select
              id="node-assignee"
              value={assignedUserId}
              onChange={e => setAssignedUserId(e.target.value)}
              width="100%"
              height={36}
            >
              <option value="">Unassigned</option>
              {members.map(m => (
                <option key={m.user_id} value={m.user_id}>{m.email}</option>
              ))}
            </Select>
          </Pane>
        )}
      </Pane>
    </Dialog>
  );
};

export default CoverageNodeDialog;
